import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Car, MapPin, Navigation, Clock, AlertTriangle, AlertCircle } from 'lucide-react';
import { driverAPI } from '../services/api';

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = '&copy; OpenStreetMap contributors';

// Decode Google-encoded polyline
const decodePolyline = (encoded) => {
  if (!encoded) return [];
  const points = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let shift = 0, result = 0, byte;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
};

const SharedTrackingPage = () => {
  const { shareToken } = useParams();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const routeLineRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const pollRef = useRef(null);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null); // 'not_found' | 'expired'
  const [loading, setLoading] = useState(true);

  // Init map
  useEffect(() => {
    if (mapInstanceRef.current) return;
    let unmounted = false;

    const initMap = () => {
      if (unmounted) return false;
      const container = mapRef.current;
      if (!container || container.offsetWidth === 0) return false;
      const L = window.L;
      if (!L) return false;

      try {
        const map = L.map(container, {
          center: [51.5074, -0.1278],
          zoom: 13,
          zoomControl: false
        });
        L.tileLayer(TILE_URL, { attribution: TILE_ATTR }).addTo(map);
        L.control.zoom({ position: 'bottomright' }).addTo(map);
        mapInstanceRef.current = map;
        setTimeout(() => { if (!unmounted && map) try { map.invalidateSize(); } catch(e) {} }, 200);
        return true;
      } catch (e) { return false; }
    };

    const timer = setInterval(() => { if (unmounted) { clearInterval(timer); return; } if (initMap()) clearInterval(timer); }, 300);
    const safety = setTimeout(() => clearInterval(timer), 10000);

    return () => {
      unmounted = true;
      clearInterval(timer);
      clearTimeout(safety);
      if (pollRef.current) clearInterval(pollRef.current);
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  // Poll tracking data
  useEffect(() => {
    if (!shareToken) return;

    const fetchData = async () => {
      try {
        const res = await driverAPI.getSharedTracking(shareToken);
        setData(res);
        setError(null);
        setLoading(false);
        updateMap(res);

        // Stop polling if ride is done
        if (['completed', 'cancelled'].includes(res.status)) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (err) {
        setLoading(false);
        const status = err.response?.status;
        if (status === 410) setError('expired');
        else if (status === 404) setError('not_found');
        else setError('not_found');
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    };

    fetchData();
    pollRef.current = setInterval(fetchData, 5000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [shareToken]);

  const updateMap = (d) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    // Driver marker
    if (d.driverLocation) {
      if (driverMarkerRef.current) driverMarkerRef.current.remove();
      const icon = L.divIcon({
        className: 'shared-driver-marker',
        html: `<div style="font-size:28px;transform:rotate(${d.driverLocation.heading || 0}deg);filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">🚕</div>`,
        iconSize: [32, 32], iconAnchor: [16, 16]
      });
      driverMarkerRef.current = L.marker([d.driverLocation.lat, d.driverLocation.lng], { icon }).addTo(map);
    }

    // Dropoff marker
    if (d.dropoff?.lat && !dropoffMarkerRef.current) {
      const icon = L.divIcon({
        className: 'custom-marker',
        html: '<div style="width:16px;height:16px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
        iconSize: [16, 16], iconAnchor: [8, 8]
      });
      dropoffMarkerRef.current = L.marker([d.dropoff.lat, d.dropoff.lng], { icon }).addTo(map);
      dropoffMarkerRef.current.bindPopup('Destination');
    }

    // Route polyline
    if (d.routeGeometry && !routeLineRef.current) {
      const points = decodePolyline(d.routeGeometry);
      if (points.length > 1) {
        routeLineRef.current = L.polyline(points, { color: '#1F3A68', weight: 5, opacity: 0.6 }).addTo(map);
      }
    }

    // Fit bounds
    const bounds = [];
    if (d.driverLocation) bounds.push([d.driverLocation.lat, d.driverLocation.lng]);
    if (d.dropoff?.lat) bounds.push([d.dropoff.lat, d.dropoff.lng]);
    if (bounds.length > 1) map.fitBounds(bounds, { padding: [50, 50] });
    else if (bounds.length === 1) map.setView(bounds[0], 14);
  };

  const getStatusLabel = (status) => {
    const map = {
      requested: 'Finding a driver...',
      accepted: 'Driver assigned',
      en_route: 'Driver on the way',
      arrived_pickup: 'Driver at pickup',
      in_progress: 'Trip in progress',
      completed: 'Trip completed',
      cancelled: 'Trip cancelled'
    };
    return map[status] || status;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              {error === 'expired' ? 'Link Expired' : 'Link Not Found'}
            </h2>
            <p className="text-gray-500">
              {error === 'expired'
                ? 'This tracking link has expired. The ride has been completed.'
                : 'This tracking link is invalid or the ride was cancelled.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Car size={24} className="text-[#1F3A68]" />
            <div>
              <h1 className="text-lg font-bold text-[#1F3A68]">Live Ride Tracking</h1>
              <p className="text-xs text-gray-500">Ariv</p>
            </div>
          </div>
        </div>
      </header>

      {/* Map */}
      <div ref={mapRef} className="w-full h-[50vh] md:h-[60vh]" />

      {/* Info Card */}
      <div className="max-w-lg mx-auto p-4 -mt-6 relative z-10">
        {loading ? (
          <Card className="shadow-lg">
            <CardContent className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68] mx-auto mb-3" />
              <p className="text-gray-500">Loading ride info...</p>
            </CardContent>
          </Card>
        ) : data && (
          <Card className="shadow-lg">
            <CardContent className="p-4 space-y-4">
              {/* Status + ETA */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-indigo-100 text-indigo-800">
                    <Navigation size={18} />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{getStatusLabel(data.status)}</p>
                    {data.vehicleType && <p className="text-xs text-gray-500 capitalize">{data.vehicleType}</p>}
                  </div>
                </div>
                {data.etaMinutes && !['completed', 'cancelled'].includes(data.status) && (
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#1F3A68]">{data.etaMinutes}</p>
                    <p className="text-xs text-gray-500">min ETA</p>
                  </div>
                )}
              </div>

              {/* Traffic badge */}
              {data.inTraffic && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 p-2.5 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Driver is in slow traffic</span>
                </div>
              )}

              {/* Vehicle info */}
              {data.vehiclePlate && (
                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-[#1F3A68] text-white flex items-center justify-center text-lg">
                    🚕
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{data.driverName || 'Your driver'}</p>
                    <Badge variant="secondary">{data.vehiclePlate}</Badge>
                  </div>
                </div>
              )}

              {/* Destination (no pickup shown for privacy) */}
              {data.dropoff?.address && (
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-1.5 rounded-full bg-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Heading to</p>
                    <p className="text-sm font-medium">{data.dropoff.address}</p>
                  </div>
                </div>
              )}

              {/* Completed message */}
              {data.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-center">
                  <p className="font-bold text-green-800">Trip Completed</p>
                  <p className="text-xs text-green-600 mt-1">The passenger has arrived safely</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SharedTrackingPage;
